<script lang="ts" setup>
import { createNavigationMenu } from "@sit-onyx/headless";
import { iconChevronLeftSmall, iconMenu, iconMoreVertical } from "@sit-onyx/icons";
import { computed, provide, ref, toRef, useTemplateRef, watch } from "vue";
import { useLink } from "../../composables/useLink.js";
import { useResizeObserver } from "../../composables/useResizeObserver.js";
import { injectI18n } from "../../i18n/index.js";
import { ONYX_BREAKPOINTS } from "../../utils/breakpoints.js";
import OnyxMobileNavButton from "../OnyxMobileNavButton/OnyxMobileNavButton.vue";
import OnyxMoreList from "../OnyxMoreList/OnyxMoreList.vue";
import OnyxNavAppArea from "../OnyxNavAppArea/OnyxNavAppArea.vue";
import OnyxNavButton from "../OnyxNavButton/OnyxNavButton.vue";
import OnyxFlyoutMenu from "./modules/OnyxFlyoutMenu/OnyxFlyoutMenu.vue";
import OnyxNavItemFacade from "./modules/OnyxNavItemFacade/OnyxNavItemFacade.vue";
import {
  MOBILE_NAV_BAR_INJECTION_KEY,
  NAV_BAR_MORE_LIST_INJECTION_KEY,
  NAV_BAR_MORE_LIST_TARGET_INJECTION_KEY,
  type OnyxNavBarProps,
  type OnyxNavBarSlots,
} from "./types.js";

const props = withDefaults(defineProps<OnyxNavBarProps<"horizontal">>(), {
  mobile: "sm",
});

const emit = defineEmits<{
  /**
   * Emitted when the back button is clicked.
   */
  navigateBack: [event: MouseEvent];
}>();

const slots = defineSlots<OnyxNavBarSlots>();

const navBar = useTemplateRef("navBar");
const { width } = useResizeObserver(navBar);
const { t } = injectI18n();
const { currentRoute } = useLink();

const {
  elements: { nav },
} = createNavigationMenu({ navigationName: toRef(() => props.appName) });

const isBurgerOpen = ref(false);
const isContextOpen = ref(false);

const isMobileWidth = (breakpoint: number, currentWidth: number) =>
  currentWidth !== 0 && currentWidth < breakpoint;

const actualIsMobile = computed(() => {
  if (typeof props.mobile === "number") {
    return isMobileWidth(props.mobile, width.value);
  }
  if (typeof props.mobile === "string") {
    return isMobileWidth(ONYX_BREAKPOINTS[props.mobile], width.value);
  }
  return props.mobile;
});

provide(MOBILE_NAV_BAR_INJECTION_KEY, actualIsMobile);
provide(NAV_BAR_MORE_LIST_TARGET_INJECTION_KEY, useTemplateRef("moreList"));

const closeMobileMenus = () => {
  isBurgerOpen.value = false;
  isContextOpen.value = false;
};

watch(currentRoute, () => closeMobileMenus(), { deep: true });

defineExpose({
  /**
   * Closes the mobile burger and context menu.
   * Useful if you want to e.g. close them when a nav item is clicked.
   * Will be automatically done if a router is provided.
   */
  closeMobileMenus,
});
</script>

<template>
  <header
    ref="navBar"
    class="onyx-component onyx-nav-bar"
    :class="{ 'onyx-nav-bar--mobile': actualIsMobile }"
  >
    <div class="onyx-nav-bar__content onyx-grid-container">
      <span
        v-if="actualIsMobile && slots.mobileActivePage && !isBurgerOpen && !isContextOpen"
        class="onyx-nav-bar__mobile-page onyx-truncation-ellipsis"
      >
        <slot name="mobileActivePage"></slot>
      </span>

      <OnyxNavAppArea
        v-else-if="props.appName || props.logoUrl || slots.appArea"
        class="onyx-nav-bar__app"
        :app-name="props.appName"
        :logo-url="props.logoUrl"
        v-bind="props.appArea"
      >
        <slot name="appArea"></slot>
      </OnyxNavAppArea>

      <OnyxNavButton
        v-if="props.withBackButton"
        class="onyx-nav-bar__back"
        :label="t('navigation.goBack')"
        :icon="iconChevronLeftSmall"
        hide-label
        @click="emit('navigateBack', $event)"
      />

      <template v-if="slots.default">
        <OnyxMobileNavButton
          v-if="actualIsMobile"
          v-model:open="isBurgerOpen"
          class="onyx-nav-bar__burger"
          :icon="iconMenu"
          :label="t('navigation.toggleBurgerMenu')"
          :headline="t('navigation.navigationHeadline')"
          @update:open="isContextOpen = false"
        >
          <nav class="onyx-nav-bar__nav--mobile" v-bind="nav">
            <ul role="menubar">
              <slot></slot>
            </ul>
          </nav>
        </OnyxMobileNavButton>

        <nav v-else class="onyx-nav-bar__nav" v-bind="nav">
          <OnyxMoreList is="ul" role="menubar" :injection-key="NAV_BAR_MORE_LIST_INJECTION_KEY">
            <template #default="{ attributes }">
              <div v-bind="attributes">
                <slot></slot>
              </div>
            </template>
            <template #more="{ attributes, hiddenElements }">
              <OnyxFlyoutMenu :label="t('navigation.showMoreNavItemsLabel')" v-bind="attributes">
                <template #button="{ trigger }">
                  <OnyxNavItemFacade
                    v-bind="trigger"
                    :label="t('navigation.moreNavItems', { n: hiddenElements })"
                    context="navbar"
                  />
                </template>

                <template #options>
                  <div ref="moreList"></div>
                </template>
              </OnyxFlyoutMenu>
            </template>
          </OnyxMoreList>
        </nav>
      </template>

      <template v-if="slots.contextArea || slots.globalContextArea">
        <div v-if="actualIsMobile" class="onyx-nav-bar__mobile-context">
          <div v-if="slots.globalContextArea" class="onyx-nav-bar__mobile-global-context">
            <slot name="globalContextArea"></slot>
          </div>

          <OnyxMobileNavButton
            v-if="slots.contextArea"
            v-model:open="isContextOpen"
            :icon="iconMoreVertical"
            :label="t('navigation.toggleContextMenu')"
            @update:open="isBurgerOpen = false"
          >
            <div class="onyx-nav-bar__mobile-context-content">
              <slot name="contextArea"></slot>
            </div>
          </OnyxMobileNavButton>
        </div>

        <div v-else class="onyx-nav-bar__context">
          <slot v-if="slots.globalContextArea" name="globalContextArea"></slot>
          <slot v-if="slots.contextArea" name="contextArea"></slot>
        </div>
      </template>
    </div>
  </header>
</template>

<style lang="scss">
@use "../../styles/mixins/layers";

$gap: var(--onyx-spacing-md);

.onyx-nav-bar {
  @include layers.component() {
    .onyx-flyout-menu {
      --onyx-flyout-menu-gap: var(--onyx-spacing-md);
    }

    background-color: var(--onyx-color-base-background-blank);
    font-family: var(--onyx-font-family);
    color: var(--onyx-color-text-icons-neutral-intense);
    height: var(--onyx-nav-bar-height);
    z-index: var(--onyx-z-index-navigation);
    position: relative;
    container-type: inline-size;

    // implement bottom border with ::after so it does not add to the height
    &::after {
      content: " ";
      background-color: var(--onyx-color-component-border-neutral);
      height: var(--onyx-1px-in-rem);
      width: 100%;
      position: absolute;
      bottom: 0;
    }

    &__content {
      display: grid;
      grid-template-columns: max-content 1fr auto;
      grid-template-areas: "app nav context";
      align-items: center;
      gap: $gap;
      height: 100%;
      padding-inline: var(--onyx-grid-margin);

      &:has(.onyx-nav-bar__back) {
        grid-template-columns: max-content max-content 1fr auto;
        grid-template-areas: "app back nav context";
      }
    }

    &__back {
      grid-area: back;
    }

    &__nav {
      grid-area: nav;

      > ul {
        display: flex;
        align-items: center;
        gap: var(--onyx-spacing-4xs);
        padding: 0;
      }

      &--mobile {
        display: flex;
        flex-direction: column;
        gap: var(--onyx-spacing-2xs);

        > ul {
          display: contents;
        }
      }
    }

    // fix outline being cut-off by the clipping
    .onyx-more-list__elements {
      padding-inline: 0.25rem;
    }

    &__context {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: var(--onyx-spacing-4xs);
      grid-area: context;
    }

    &__app {
      border-right: var(--onyx-1px-in-rem) solid var(--onyx-color-component-border-neutral);
      grid-area: app;
    }

    &--mobile {
      .onyx-nav-bar__content {
        grid-template-columns: max-content max-content auto auto;
        grid-template-areas: "burger back nav mobile-context";
        gap: 0;
        padding-inline: 0;

        .onyx-nav-bar__back {
          margin-left: $gap;
        }
      }

      .onyx-nav-bar__app {
        border-right: none;
        grid-area: nav;
      }
    }

    &__burger {
      border-right: var(--onyx-1px-in-rem) solid var(--onyx-color-component-border-neutral);
      grid-area: burger;
    }

    &__mobile-context {
      grid-area: mobile-context;
      display: flex;
      align-items: center;
      margin-left: auto;
      gap: var(--onyx-spacing-4xs);
    }

    &__mobile-global-context {
      display: flex;
      flex-direction: row-reverse;
      gap: inherit;
    }

    &__mobile-context-content {
      display: flex;
      flex-direction: row-reverse;
      flex-wrap: wrap-reverse;
      align-items: center;
      justify-content: flex-end;
      gap: var(--onyx-spacing-2xs);

      // add extra spacing after the user menu if it is not immediately followed by a nav separator
      // since we are in a row-reverse layout, we need to "separator + user menu" as selector
      // to check if a nav separator exists "visually" after the user menu
      &:not(:has(.onyx-separator + .onyx-user-menu)) {
        .onyx-user-menu {
          margin-bottom: var(--onyx-spacing-md);
        }
      }
    }

    &__mobile-page {
      grid-area: nav;
      color: var(--onyx-color-text-icons-primary-intense);
      padding-inline: $gap;
      font-weight: var(--onyx-font-weight-semibold);
    }

    .onyx-mobile-nav-button {
      --top-position: var(--onyx-nav-bar-height);
    }

    .onyx-separator {
      margin: var(--onyx-spacing-2xs) 0;
      width: 100%;

      &--vertical {
        margin: 0 var(--onyx-spacing-2xs);
        width: var(--onyx-separator-size);
      }
    }
  }
}
</style>
